// src/components/.../FeedsTable.js
import * as React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';

import * as WrcCommon from '@webrcade/app-common';
import { Global } from '../../Global';
import CommonTable from '../common/CommonTable';
import * as Feed from '../../Feed';
import ImageLabel from '../common/ImageLabel';
import Prefs from '../../Prefs';
import ToolbarVerticalDivider from '../common/ToolbarVerticalDivider';

import EditorSwitch from '../common/editor/EditorSwitch';

// 🔹 i18n
import { useI18n } from '../../i18n/I18nContext';

function createData(id, title, thumbSrc, location, localId) {
  return {
    id,
    title,
    thumbSrc,
    location,
    localId,
  };
}

const PREF_SHOW_REMOTE = 'loadFeeds.showRemote';

export default function FeedsTable(props) {
  const { feeds, setOpen, onDelete } = props;
  const [showRemote, setShowRemote] = React.useState(
    Prefs.getBoolPreference(PREF_SHOW_REMOTE, true)
  );

  const { t } = useI18n(); // 🔹 hook de tradução

  const rows = [];
  if (feeds) {
    const distinctFeeds = feeds.getDistinctFeeds();
    distinctFeeds.forEach((f) => {
      if (showRemote || !f.url) {
        rows.push(
          createData(
            f.feedId,
            f.title,
            f.thumbnail,
            f.url ? f.url : '',
            f.localId
          )
        );
      }
    });
  }

  return (
    <CommonTable
      tableName="feeds"
      defaultSortColumn="title"
      resetKey={null}
      size={'small'}
      headCells={[
        {
          id: 'title',
          numeric: false,
          disablePadding: true,
          sortable: true,
          label: t('feedsTableHeaderFeed'), // 🔹 "Feed"
          width: '1%',
        },
        {
          id: 'load',
          numeric: false,
          disablePadding: false,
          label: t('feedsTableHeaderLoad'), // 🔹 "Load"
          width: '1%',
        },
        {
          id: 'location',
          numeric: false,
          disablePadding: false,
          sortable: true,
          label: t('feedsTableHeaderLocation'), // 🔹 "Location"
        },
      ]}
      rows={rows}
      renderRow={(row, index) => {
        return (
          <>
            <TableCell
              component="th"
              id={row.id}
              scope="row"
              padding="checkbox"
              style={{ width: '0%', whiteSpace: 'noWrap' }}
            >
              <ImageLabel
                label={row.title}
                imageSrc={row.thumbSrc}
                defaultImageSrc={WrcCommon.FeedThumbImage}
              />
            </TableCell>
            <TableCell style={{ width: '0%', whiteSpace: 'noWrap' }}>
              <Tooltip title={t('feedsTableTooltipLoad')}>
                <IconButton
                  onClick={async (e) => {
                    e.stopPropagation();
                    Global.openBusyScreen(true, t('feedsTableBusyLoading'));
                    try {
                      let feed = null;
                      if (row.localId) {
                        const localFeed = await feeds.getLocalFeed(row.localId);
                        if (localFeed) {
                          feed = await Feed.loadFeed(localFeed);
                        }
                      } else if (row.location && row.location.trim().length > 0) {
                        feed = await Feed.loadFeedFromUrl(row.location);
                      }
                      if (feed) {
                        Global.setFeed({ ...feed });
                        setOpen(false);
                      } else {
                        Global.displayMessage(
                          t('feedsTableErrorNoLongerExists'),
                          'error'
                        );
                      }
                    } catch (e) {
                      Global.displayMessage(
                        t('feedsTableErrorLoad'),
                        'error'
                      );
                    } finally {
                      Global.openBusyScreen(false);
                    }
                  }}
                >
                  <FileOpenIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
            <TableCell>
              {row.location ? row.location : t('feedsTableLocationLocal')}
            </TableCell>
          </>
        );
      }}
      renderToolbarItems={(selection, selected) => {
        return (
          <>
            <div>
              <EditorSwitch
                label={t('feedsTableSwitchShowRemoteLabel')}
                tooltip={t('feedsTableSwitchShowRemoteTooltip')}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setShowRemote(checked);
                  Prefs.setPreference(PREF_SHOW_REMOTE, checked);
                }}
                checked={showRemote}
              />
            </div>
            <ToolbarVerticalDivider />
            <Tooltip title={t('feedsTableToolbarDelete')}>
              <div>
                <IconButton
                  disabled={!selection}
                  onClick={() => {
                    const single = selected.length === 1;
                    const title = single
                      ? t('feedsTableDeleteTitleSingle')
                      : t('feedsTableDeleteTitlePlural');
                    const message = single
                      ? t('feedsTableDeleteMessageSingle')
                      : t('feedsTableDeleteMessagePlural');
                    const busy = single
                      ? t('feedsTableBusyDeletingSingle')
                      : t('feedsTableBusyDeletingPlural');

                    Global.openConfirmDialog(
                      true,
                      title,
                      message,
                      async () => {
                        try {
                          Global.openBusyScreen(true, busy);
                          for (let i = 0; i < selected.length; i++) {
                            const id = selected[i];
                            await feeds.removeFeed(id);
                          }
                          if (onDelete) await onDelete();
                        } finally {
                          Global.openBusyScreen(false);
                        }
                      }
                    );
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            </Tooltip>
            <ToolbarVerticalDivider />
          </>
        );
      }}
    />
  );
}